import { useCallback } from "react";

import { PlayStateApi } from "../../../hooks";
import { Frame, MarkerType, MovementType } from "../../../types";
import { createMovementScale, getBallMarkers, getMarkerStencils, isAnimateableStencil } from "../../../utils";

export interface UseFastBuild extends Pick<PlayStateApi, "setPlayState"> {
  activeFrame: Frame | null;
  playFrames: Frame[];
  setActiveFrameId: (id: string) => void;
}

export const useFastBuild = ({ activeFrame, playFrames, setActiveFrameId, setPlayState }: UseFastBuild) => {
  const fastBuild = useCallback(async () => {
    const frameStencils = activeFrame?.frameStencils || [];

    // get all markers to be used in fast build
    const markers = getMarkerStencils(frameStencils);

    // update all player and ball markers to end positions (other markers remain unchanged)
    const updatedStencils = markers.map((marker) => {
      if (!isAnimateableStencil(marker)) {
        return marker;
      }

      const { markerType } = marker;
      const scale = createMovementScale({ marker, stencils: frameStencils });
      const baseMarker = { ...marker, movements: [], pos: scale(Infinity) };

      switch (markerType) {
        case MarkerType.PLAYER:
          return { ...baseMarker, possession: false };
        case MarkerType.BALL: {
          const ballsLastPlayer = marker.movements[marker.movements.length - 1]?.id;
          return {
            ...baseMarker,
            associatedPlayer: ballsLastPlayer || "",
            movements: ballsLastPlayer
              ? [{ id: ballsLastPlayer, movementType: MovementType.POSSESSION, startTime: 0 }]
              : [],
          };
        }
      }
    });

    // get all ball markers' associated players
    const ballStencilAssociatedPlayers = getBallMarkers(updatedStencils).map((stencil) => stencil.associatedPlayer);
    // update stencils with new possession
    const updatedStencilsWithPossession = updatedStencils.map((stencil) => {
      const { markerType } = stencil;
      switch (markerType) {
        case MarkerType.PLAYER: {
          if (ballStencilAssociatedPlayers.includes(stencil.id)) return { ...stencil, possession: true };
          else return stencil;
        }
        default:
          return stencil;
      }
    });

    const activePlayFrameIndex = playFrames.findIndex((frame) => frame.id === activeFrame?.id);
    const nextPlayFrame = playFrames[activePlayFrameIndex + 1];

    if (nextPlayFrame) {
      try {
        const newPlayFrames = playFrames.map((playFrame) => {
          if (playFrame.id === nextPlayFrame.id) {
            return { ...playFrame, frameStencils: updatedStencilsWithPossession };
          } else return playFrame;
        });

        setPlayState((prevState) => ({ ...prevState, frames: newPlayFrames }));
        setActiveFrameId(nextPlayFrame.id);
      } catch (err) {
        console.error(err);
      }
    }
  }, [activeFrame, playFrames, setPlayState, setActiveFrameId]);

  return { fastBuild };
};
